import React, { memo } from 'react';
import cn from 'classnames';
import format from 'date-fns/format';
import formatDistance from 'date-fns/formatDistance';

import { Icon, Image, Link } from 'components';
import Chip from 'components/Chip';
import { Col, Row } from 'components/layout';
import Avatar from 'components/Avatar';
import Salary from 'components/Salary';
import { TouchpointType } from 'lib/models/touchpoint';
import { parseArrayResponse, parseResponse } from 'lib/utils/parser';
import { CitiesResponse } from 'lib/models/city';
import { TagAttributes, TagsResponse, TagType } from 'lib/models/tag';
import {
  UniversityAttributes,
  UniversityResponse,
} from 'lib/models/university';
import { NestedType } from 'lib/models/matches';
import { getCardLabel } from 'utils/format';
import Deadline from '../../Deadline';
import DatePosted from '../../DatePosted';
import styles from './MatchCard.module.scss';

export interface MatchCardProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  /** url for company/society (createdBy) icon */
  icon_url: string;
  /** title of the opportunity */
  title: string;
  /** name of the company/society */
  createdBy: string;
  /** start date of the opportunity */
  startDate: string;
  end_date?: string;
  created_at: string;
  application_deadline: string;
  maximum_salary?: number | null;
  minimum_salary?: number | null;
  pay_currency?: string;
  is_verified: boolean;
  nested_type: TouchpointType | NestedType.STUDENT_SOCIETY | NestedType.COMPANY;
  description: string;
  cities?: CitiesResponse;
  /** public url for opportunity/company/society */
  url: string;
  topics?: TagsResponse<TagType.Topic>;
  /** Society Company logo for Society-Company Card */
  SocietyCompanyLogo: string;
  university: UniversityResponse;
}

enum Location {
  ONLINE = 'Online',
}

export const MatchCard: React.FC<MatchCardProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
    icon_url,
    title,
    createdBy,
    startDate,
    end_date,
    maximum_salary,
    minimum_salary,
    pay_currency,
    created_at,
    application_deadline,
    is_verified,
    nested_type,
    url,
    description,
    cities,
    topics: topicsResponse,
    SocietyCompanyLogo,
    university: universityResponse,
  }: MatchCardProps) => {
    const contentClassNames = cn(styles.MatchCard, className);

    const startDateValue = startDate
      ? format(new Date(startDate), 'MMM d')
      : null;

    const startDateTimeValue = startDate
      ? format(new Date(startDate), 'MMM d, h:mma')
      : null;

    const location = parseArrayResponse(cities);
    const locationDetail = location.map(({ id, name }) => {
      return (
        <Chip key={id} className={styles.chip}>
          {name !== Location.ONLINE ? '📍' : '💻'}
          <span className={styles.name}>{name}</span>
        </Chip>
      );
    });

    const duration =
      startDate && end_date
        ? formatDistance(new Date(startDate), new Date(end_date))
        : null;

    const topics =
      parseArrayResponse<TagAttributes<TagType.Topic>>(topicsResponse);
    const shouldTopicRender =
      topics.length > 0 &&
      (nested_type === TouchpointType.Event ||
        nested_type === 'StudentSociety' ||
        nested_type === 'Company');

    const topicsList = topics.map(({ id, name }) => {
      return (
        <Chip key={id} className={styles.chip}>
          <span className={styles.name}>{name}</span>
        </Chip>
      );
    });

    const university = parseResponse<UniversityAttributes>(universityResponse);

    const getInitials = (name: string) => (name ? name.split(' ')[0] : '');

    return (
      <Link href={url} underlineOnHover={false}>
        <div
          className={contentClassNames}
          style={style}
          data-testid="MatchCard"
        >
          <Row align="center">
            <p
              className={cn(styles.type, {
                [styles.event]: nested_type === TouchpointType.Event,
                [styles.job]: nested_type === TouchpointType.Job,
                [styles.internship]: nested_type === TouchpointType.Internship,
                [styles.society]: nested_type === 'StudentSociety',
                [styles.company]: nested_type === 'Company',
              })}
            >
              {getCardLabel(nested_type)}
            </p>
            {startDate && (
              <p className={styles.startdate}>
                • Starts{' '}
                {nested_type === TouchpointType.Event
                  ? startDateTimeValue
                  : startDateValue}
              </p>
            )}
          </Row>
          {nested_type !== 'StudentSociety' && nested_type !== 'Company' ? (
            <>
              <p className={styles.title}>{title}</p>
              <Row align="center" className={styles.info}>
                {icon_url ? (
                  <Image
                    src={icon_url}
                    height="24"
                    width="24"
                    className={styles.logo}
                  />
                ) : (
                  <Avatar
                    name={getInitials(createdBy)}
                    size="small"
                    className={styles.logo}
                  />
                )}
                {createdBy && (
                  <>
                    <p>{createdBy}</p>
                    {is_verified ? (
                      <Icon iconName="verified" className={styles.verified} />
                    ) : null}
                  </>
                )}
                {created_at && (
                  <>
                    <div className={styles.dot}>•</div>
                    <DatePosted created_at={created_at} />
                  </>
                )}
              </Row>
              {(location.length > 0 || duration) && (
                <Row align="center" className={styles.location}>
                  {locationDetail}
                  {nested_type === TouchpointType.Event && duration && (
                    <Chip className={styles.chip}>⏱ {duration}</Chip>
                  )}
                </Row>
              )}
            </>
          ) : (
            <Row align="center">
              {SocietyCompanyLogo ? (
                <Col sm={6}>
                  <Image
                    src={SocietyCompanyLogo}
                    height="64"
                    width="64"
                    className={styles.societyCompanyLogo}
                  />
                </Col>
              ) : (
                <Col sm={6}>
                  <Avatar
                    name={getInitials(title)}
                    size="xxlarge"
                    className={styles.societyCompanyLogo}
                  />
                </Col>
              )}
              <Col sm={18}>
                <p className={styles.societyCompanyTitle}>{title}</p>
                <p className={styles.societyCompanyInfo}>{university?.name}</p>
                <p className={styles.societyCompanyInfo}>
                  {location.length > 0 && location[0].name}
                </p>
              </Col>
            </Row>
          )}
          <div className={styles.separator}></div>
          {minimum_salary ||
          maximum_salary ||
          application_deadline ||
          shouldTopicRender ? (
            <Row align="center" className={styles.detail}>
              {minimum_salary || maximum_salary ? (
                <Salary
                  minimum_salary={minimum_salary}
                  maximum_salary={maximum_salary}
                  pay_currency={pay_currency}
                  className={styles.chip}
                />
              ) : null}
              {shouldTopicRender && topicsList}
              {application_deadline && (
                <Deadline application_deadline={application_deadline} />
              )}
            </Row>
          ) : null}
          <div className={styles.descriptionContainer}>
            <div className={styles.description}>{description}</div>
          </div>
        </div>
      </Link>
    );
  }
);

MatchCard.displayName = 'MatchCard';
